import { Box, Grid, Button, TextField, InputAdornment, SvgIcon, Autocomplete, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, Typography, useTheme, IconButton, Tooltip } from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import { SearchSm } from '../../../../assets';
import CustomPagination from '../../../../components/forms/Pagination';
import { UserContext } from '../../../../components/shared/useUser';
import LoadingWheel from '../../../../components/ui/LoadingWheel';
import useLogError from '../../../../hooks/useLogError';
import { useContext, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ProductRow from './ProductRow';
import useCurrencyCodeConfiguration from '../../../../hooks/configurations/useCurrencyCodeConfiguration';
import { starbucksFranchises } from '../../../../enums/FranchiseNames';
import { OrderCategoriesTranslationMap, OrdersCategoriesParentsMap, isOrderCategory, isOrderCategoryParent } from '../../../../enums/OrderCategories';
import { PredictedDemandModel } from '../../../../app/services/api/podClient';
import { ItemsFilterOptions, client, Item, Category, OrderDetails, ContainerFillResult, ShippingDetailsRequest } from '../../../../app/services/api/orderManagementClient';
import { PromotionForCustomerDto } from '../../../../app/services/api/promotions';
import { useNavigate } from 'react-router-dom';
import { useActiveOrder } from '../../../../contexts/ActiveOrderContext';
import { OrderStatus } from '../../../../enums/OrderStatus';
import { exportFile } from '../../../../utils';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import { normalizeDeliveryDate } from '../../../../hooks/useDeliveryPricing';
import { DateOnly } from '../../../../app/services/api/ofdClient';
const isPromoOrderItem = (item: Pick<OrderDetails['items'][number], 'promotionAllocationId'> | { promotionAllocationId?: number | null }) => item.promotionAllocationId != null;

const Products = ({ orderIdString }: { orderIdString: string | undefined }) => {

  const orderId = orderIdString ? parseInt(orderIdString, 10) : 0;
  const theme = useTheme();
  const navigate = useNavigate();
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const canEditPredictions = hasPermissionTo(['AIOrderingAdmin', 'AIOrderingEditPredictions']);
  const { logError } = useLogError();
  const { t } = useTranslation('products');
  const { t: tOrderDetails } = useTranslation('orderDetails');
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const { setActiveOrder, setContainerData: setActiveContainerData, setShippingDetails } = useActiveOrder();
  const { getRetailerIdByStore, deliveryPricingClient } = useNswagClient();
  const getRetailerIdByStoreRef = useRef(getRetailerIdByStore);
  const deliveryPricingClientRef = useRef(deliveryPricingClient);

  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const pageReset = useRef(false);
  const productRowRefs = useRef<{ matchPredictedQuantities: () => void }[]>([]);

  const [itemSearch, setItemSearch] = useState<string>('');
  const [sortBy, setSortBy] = useState<'asc' | 'desc'>('asc');
  const [category, setCategory] = useState<number>(-1); const [categories, setCategories] = useState<Category[]>([]);
  const [localActiveOrder, setLocalActiveOrder] = useState<OrderDetails>();
  const [supplier, setSupplier] = useState<string>('');
  const [showPredictedQuantity] = useState<boolean>(true);
  const [predictedDemands] = useState<PredictedDemandModel[]>([]);
  const [orderDetails, setOrderDetails] = useState<OrderDetails>();
  const [isDownloadingSpreadsheet, setIsDownloadingSpreadsheet] = useState<boolean>(false);
  const [isUploadingSpreadsheet, setIsUploadingSpreadsheet] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const pageSize = 10;
  const isStarBucksRetail = starbucksFranchises.includes(selectedStore?.franchiseName ?? '');
  const showMessageWhenNoCost = isStarBucksRetail && localActiveOrder?.status != 'Order Confirmed'; const [items, setItems] = useState<Item[]>([]);
  const [orderTypeId, setOrderTypeId] = useState<number>(0);

  const scrollOnTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };

  const getFilterOptions = (storeNumber: string, itemSearchString: string, categoryId: number, currentOrderType: number, currentSupplier: string, pageNumber: number, currentPageSize: number, sort: string, direction: string): ItemsFilterOptions => {
    return {
      storeNumber: storeNumber,
      item: itemSearchString,
      categoryId: categoryId,
      orderType: currentOrderType,
      supplier: currentSupplier,
      pageNumber: pageNumber,
      pageSize: currentPageSize,
      sort: sort,
      direction: direction,
    };
  };

  const sortItemsByActiveOrder = (allItems: Item[], selectedOrder: OrderDetails) => {
    const sortedItems: Item[] = [];
    const existingItems = allItems.filter(item =>
      selectedOrder.items.some(i => i.sku === item.sku),
    );
    const possibleItems = allItems.filter((element) => !existingItems.includes(element));
    sortedItems.push(...existingItems, ...possibleItems);
    setItems(sortedItems);
  };

  const fetchProducts = async (filters: ItemsFilterOptions, activeOrder?: OrderDetails) => {
    try {
      const itemsResponse = await client.getItems(filters);
      setItems(itemsResponse.data.items);
      setTotalPages(itemsResponse.data.totalPages);
      setTotalCount(itemsResponse.data.totalCount);
      if (page !== itemsResponse.data.pageNumber) {
        pageReset.current = true;
        setPage(itemsResponse.data.pageNumber);
      }
      if (activeOrder) {
        sortItemsByActiveOrder(itemsResponse.data.items, activeOrder);
      }
    } catch (error) {
      logError(error);
      console.error(t('fetchingErrorMessage') + ' ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  const activateShippingMethod = async (
    selectedStore?: string,
    order?: OrderDetails,
    containerData?: ContainerFillResult,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    shippingData?: any,
    retailerId?: number,
  ) => {
  
    if (!containerData || !shippingData) return;

    try {
      const flatContainerCharge = parseFloat(shippingData.deliveryCharge) || 0;
      // Do not floor to 1 — an order with 0 containers (nothing added yet, or everything removed)
      // must total £0, not 1 container's worth of charge.
      const count = containerData.totalContainersUsed > 0 ? containerData.totalContainersUsed : 0;

      let containerCharge = flatContainerCharge;
      let totalChargeValue = flatContainerCharge * count;

      // Try the pricing engine first, same as OrderDetailsOfd — falls back to the flat rate
      // × container count only if no rule resolves. Without this, this screen and the order/basket
      // view can show two different delivery charges for the same order.
      // retailerId is resolved fresh by the caller (awaited immediately before this call) rather than
      // read from reactive state — a separate/earlier retailerId lookup effect race meant this screen
      // could resolve against the wrong (e.g. global instead of retailer-specific) rule.
      const shippingMethodId = Number(shippingData.deliveryMethodId);
      // order.deliveryDate can be DD-MM-YYYY (once a delivery date has been set/saved) or ISO
      // yyyy-MM-dd (a brand new order's default). The API's DateOnly binder only accepts ISO —
      // normalize here the same way useDeliveryPricing does, otherwise setting a delivery date
      // makes every subsequent resolve() call 400.
      const normalizedDeliveryDate = normalizeDeliveryDate(
        order?.deliveryDate ?? new Date().toISOString().split('T')[0],
      );
      if (containerData.totalPalletsNeeded > 0 && shippingMethodId && normalizedDeliveryDate) {
        try {
          const result = await deliveryPricingClientRef.current.resolve({
            shippingMethodId,
            retailerId,
            wholePallets: Math.floor(containerData.totalPalletsNeeded),
            lastPalletFillPercent: containerData.containerFillPercents.length > 0
              ? containerData.containerFillPercents[containerData.containerFillPercents.length - 1]
              : 100,
            deliveryDate: normalizedDeliveryDate as unknown as DateOnly,
            baseCharge: flatContainerCharge,
          });
          if (result.resolved) {
            totalChargeValue = result.totalCharge ?? 0;
            containerCharge = count > 0 ? totalChargeValue / count : totalChargeValue;
          }
        } catch (engineError) {
          logError(engineError);
        }
      }

      setShippingDetails({
        deliveryMethod: shippingData.deliveryMethod || '',
        containerCharge: containerCharge.toString(),
        totalDeliveryCharge: totalChargeValue.toFixed(2),
      });
      const payload: ShippingDetailsRequest = {
        deliveryCharge: containerCharge,
        totalDeliveryCharge: totalChargeValue,
        deliveryMethod: shippingData.deliveryMethod || '',
        deliveryMethodId: shippingData.deliveryMethodId || '', 
        orderStatus: order?.statusId.toString() ?? '',
        deliveryOptions: shippingData.deliveryOptions || [], 
        country: shippingData.country || '', 
        location: shippingData.location || '',
        ...(shippingData.containerType && shippingData.containerType !== 0 && { containerType: shippingData.containerType }),
        ...(shippingData.palletType && shippingData.palletType !== 0 && { palletType: shippingData.palletType }),
      };
      await client.setShippingDetails(
        selectedStore ?? '',
        order?.id?.toString() ?? '',
        payload,
      );
    }
    catch (error) {
      logError(error);
    }
  };

  const fetchCategories = async (selectedOrderType: number, currentSupplier: string) => {
    try {
      const categoriesResponse = await client.getCategories(selectedStore?.storeNumber, selectedOrderType, currentSupplier);
      setCategories(categoriesResponse.data);
    } catch (error) {
      setCategories([]);
    }
  };

  const loadData = async (storeNumber: string | undefined, itemSearchString: string, categoryId: number, currentOrderTypeId: number, currentSupplier: string, pageNumber: number, currentPageSize: number, sort: string, direction: string) => {
    if (storeNumber) {
      setLoading(true);
      await fetchProducts(getFilterOptions(storeNumber, itemSearchString, categoryId, currentOrderTypeId, currentSupplier, pageNumber, currentPageSize, sort, direction));
      await fetchCategories(currentOrderTypeId, currentSupplier);
      scrollOnTop();
      setLoading(false);
    }
  };

  const fetchOrderDetails = async () => {

    if (!selectedStore?.storeNumber || !orderId) {
      return;
    }

    try {

      const result = await client.getOrder(selectedStore.storeNumber, orderId);
      setOrderDetails(result.data);
      const currentOrderTypeId = result.data.orderTypeId;
      const currentSupplier = result.data.supplierNumber;
      setOrderTypeId(currentOrderTypeId);
      setLocalActiveOrder(result.data);
      setActiveOrder(result.data);
      setSupplier(currentSupplier);

      if (selectedStore?.storeNumber && result.data.id) {
        try {
          const containerResult = await client.getStoreContainerData(
            selectedStore.storeNumber,
            result.data.id.toString(),
          );
          setActiveContainerData(containerResult.data);

          const shippingResult = await client.getShippingDetails(selectedStore.storeNumber, result.data.id.toString(), '');
          if (shippingResult.success && shippingResult.data) {
            const deliveryChargeStr = shippingResult.data.deliveryCharge?.toString() || '0';
            const totalDeliveryChargeNum = parseFloat(shippingResult.data.totalDeliveryCharge?.toString() || '0') || 0;
            const totalDeliveryChargeStr = totalDeliveryChargeNum > 0 ? totalDeliveryChargeNum.toString() : deliveryChargeStr;
            setShippingDetails({
              deliveryMethod: shippingResult.data.deliveryMethod || '',
              containerCharge: deliveryChargeStr,
              totalDeliveryCharge: totalDeliveryChargeStr,
            });
          }

          await activateShippingMethod(
            selectedStore.storeNumber,
            result.data,
            containerResult.data,
            shippingResult.data,
            await getRetailerIdByStoreRef.current(selectedStore.storeNumber).catch(() => undefined),
          );

        } catch (err) {
          logError(err);
          console.error(t('fetchingErrorMessage') + ' ' + err);
        }
      }

      await fetchProducts(
        getFilterOptions(selectedStore.storeNumber, '', -1, currentOrderTypeId, currentSupplier, 0, pageSize, 'Name', 'asc'),
        result.data,
      );
      await fetchCategories(currentOrderTypeId, currentSupplier);

    } catch (error) {
      console.error(t('fetchingErrorMessage') + ' ' + (error instanceof Error ? error.message : String(error)));
      throw error;
    }
  };

  useEffect(() => {
    getCurrencyCodeConfiguration();
  }, []);

  useEffect(() => {
    getRetailerIdByStoreRef.current = getRetailerIdByStore;
  }, [getRetailerIdByStore]);

  useEffect(() => {
    deliveryPricingClientRef.current = deliveryPricingClient;
  }, [deliveryPricingClient]);

  useEffect(() => {
    const performInitialLoad = async () => {
      if (!selectedStore?.storeNumber) {
        return;
      }
      setLoading(true);
      try {
        if (orderId) {
          await fetchOrderDetails();
        } else {
          setLocalActiveOrder(undefined);
          setActiveOrder(null);
          setActiveContainerData(null);
          await loadData(selectedStore.storeNumber, '', -1, orderTypeId, supplier, 0, pageSize, 'Name', 'asc');
        }
      } catch (ex) {
        console.error('Error during initial load:', ex);
        console.error(t('fetchingErrorMessage') + ' ' + (ex instanceof Error ? ex.message : String(ex)));
      } finally {
        setLoading(false);
      }
    };

    performInitialLoad();
  }, [selectedStore, orderId]);

  useEffect(() => {
    const fetchItemsOnPagination = async () => {
      if (!loading && orderTypeId !== -1 && !pageReset.current && selectedStore?.storeNumber) {
        setLoading(true);
        await fetchProducts({
          item: itemSearch,
          orderType: orderTypeId,
          storeNumber: selectedStore?.storeNumber,
          categoryId: category,
          supplier: supplier,
          pageNumber: page,
          pageSize: pageSize,
          sort: 'Name',
          direction: sortBy,
        });
        setLoading(false);
      }
      pageReset.current = false;
      scrollOnTop();
    };
    if (orderTypeId !== 0 || !orderId) {
      fetchItemsOnPagination().catch((ex) => {
        console.error(ex);
        console.error(t('fetchingErrorMessage') + ' ' + (ex instanceof Error ? ex.message : String(ex)));
        setLoading(false);
      });
    }
  }, [page]);

  const handleSearch = async () => {
    await loadData(selectedStore?.storeNumber, itemSearch, category, orderTypeId, supplier, page, pageSize, 'Name', sortBy);
  };

  const clearFilters = async () => {
    setItemSearch('');
    setCategory(-1);
    setSortBy('asc');
    setPage(0);
    await loadData(selectedStore?.storeNumber, '', -1, orderTypeId, supplier, 0, pageSize, 'Name', 'asc');
  };

  const onAddItemConfirmed = async () => {
    if (!selectedStore?.storeNumber || !orderDetails?.id) return;
    const updatedOrder = await client.getOrder(selectedStore.storeNumber, orderDetails.id);
    setOrderDetails(updatedOrder.data);
    setActiveOrder(updatedOrder.data);

    if (selectedStore?.storeNumber && updatedOrder.data.id) {
      try {
        const containerResult = await client.getStoreContainerData(
          selectedStore.storeNumber,
          updatedOrder.data.id.toString(),
        );
        setActiveContainerData(containerResult.data);
        if (containerResult.data) {
          const shippingResult = await client.getShippingDetails(selectedStore.storeNumber, updatedOrder.data.id.toString(), '');
          if (shippingResult.success && shippingResult.data) {
            const deliveryChargeStr = shippingResult.data.deliveryCharge?.toString() || '0';
            const totalDeliveryChargeNum = parseFloat(shippingResult.data.totalDeliveryCharge?.toString() || '0') || 0;
            const totalDeliveryChargeStr = totalDeliveryChargeNum > 0 ? totalDeliveryChargeNum.toString() : deliveryChargeStr;
            setShippingDetails({
              deliveryMethod: shippingResult.data.deliveryMethod || '',
              containerCharge: deliveryChargeStr,
              totalDeliveryCharge: totalDeliveryChargeStr,
            });
            const retailerId = await getRetailerIdByStoreRef.current(selectedStore.storeNumber).catch(() => undefined);
            activateShippingMethod(selectedStore.storeNumber, updatedOrder.data, containerResult.data, shippingResult.data, retailerId).catch(err => {
              logError(err);
            });
          }
        }
      } catch (err) {
        logError(err);
      }
    }

    try {
      await fetchProducts(
        getFilterOptions(
          selectedStore.storeNumber,
          itemSearch,
          category,
          orderTypeId,
          supplier,
          page,
          pageSize,
          'Name',
          sortBy,
        ),
        updatedOrder.data,
      );
    } catch (err) {
      logError(err);
      sortItemsByActiveOrder(items, updatedOrder.data);
    }

    // Update localActiveOrder AFTER items list refresh so promoBasketQuantity
    // and promotion.remainingQuantity update in the same render cycle.
    setLocalActiveOrder(updatedOrder.data);
  };

  const getBasketQuantity = (itemId: number) => {
    return localActiveOrder?.items?.find(item => item.itemId === itemId && !isPromoOrderItem(item))?.quantity ?? 0;
  };

  // Returns the promo-priced quantity already in the basket for this item
  const getPromoBasketQuantity = (itemId: number) => {
    return localActiveOrder?.items
      ?.find(i => i.itemId === itemId && isPromoOrderItem(i))
      ?.quantity ?? 0;
  };

  const onAddPromoToOrder = async (product: Item, _promotion: PromotionForCustomerDto, quantity: number) => {
    if (!selectedStore?.storeNumber || !orderDetails) {
      return { success: false, message: t('addItemErrorMessage') };
    }

    try {
      const existingPromoOrderItem = localActiveOrder?.items
        ?.find(i => i.itemId === product.id && isPromoOrderItem(i));

      const addResult = await client.addOrderItem(selectedStore.storeNumber, {
        orderId: orderDetails.id,
        itemId: product.id,
        quantity,
        isPromoLine: true,
        promoUnitPrice: existingPromoOrderItem?.promoUnitPrice ?? existingPromoOrderItem?.price,
        promotionAllocationId: existingPromoOrderItem?.promotionAllocationId ?? undefined,
      });

      if (addResult.success) {
        await onAddItemConfirmed();
        return { success: true };
      } else {
        const message = addResult.message || t('addItemErrorMessage');
        logError(new Error(message));
        return { success: false, message };
      }
    } catch (err) {
      logError(err);
      return { success: false, message: t('addItemErrorMessage') };
    }
  };

  const getPromotionForItem = (item: Item): PromotionForCustomerDto | undefined => {
    if (!item.promotionId || item.promotionRemainingQuantity == null) {
      return undefined;
    }

    return {
      promotionId: item.promotionId,
      contentId: item.id,
      name: item.promotionName ?? '',
      pricingType: item.promotionPricingType ?? (item.promotionFixedPromoUnitPrice != null ? 2 : 1),
      discountPercentage: item.promotionDiscountPercentage ?? null,
      fixedPromoUnitPrice: item.promotionFixedPromoUnitPrice ?? null,
      quantityCap: item.promotionQuantityCap ?? item.promotionRemainingQuantity,
      remainingQuantity: item.promotionRemainingQuantity,
      reservationDurationMinutes: item.promotionReservationDurationMinutes ?? 0,
      promoDescription: item.promotionDescription ?? null,
      startDateUtc: item.promotionStartDateUtc ?? '',
      endDateUtc: item.promotionEndDateUtc ?? '',
      tagName: null,
      tagTextColor: null,
      tagBackgroundColor: null,
      tagIconKey: null,
    };
  };

  const autocompleteOptions = [{ id: -1, name: 'All', parentName: '' }, ...(Array.isArray(categories) ? categories : [])];

  const canEditOrder = (): boolean => {
    return orderDetails?.status === OrderStatus.Pending ||
      orderDetails?.status === OrderStatus.Review ||
      orderDetails?.status === OrderStatus.Amended;
  };

  const canShowSpreadsheetActions = (): boolean => {
    return orderDetails?.status === OrderStatus.Pending || orderDetails?.status === OrderStatus.Review;
  };

  const handleSnackbarOpen = (message: string, severity: 'error' | 'warning' | 'info' | 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleDownloadSpreadsheet = async () => {
    if (!selectedStore?.storeNumber || !orderDetails?.id) {
      handleSnackbarOpen(tOrderDetails('downloadSpreadsheetError', 'Could not download spreadsheet. Store or Order ID missing.'), 'error');
      return;
    }

    setIsDownloadingSpreadsheet(true);
    try {
      const result = await client.downloadOrderSpreadsheet(selectedStore.storeNumber, orderDetails.id.toString());
      if (result.success && result.data) {
        const binaryString = atob(result.data.fileData);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        exportFile(blob, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', result.data.fileName);
        handleSnackbarOpen(tOrderDetails('downloadSpreadsheetSuccess'), 'success');
      } else {
        handleSnackbarOpen(result.message || tOrderDetails('downloadSpreadsheetError'), 'error');
      }
    } catch (error) {
      console.error('Failed to download spreadsheet:', error);
      handleSnackbarOpen(tOrderDetails('downloadSpreadsheetError'), 'error');
    } finally {
      setIsDownloadingSpreadsheet(false);
    }
  };

  const handleUploadSpreadsheet = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedStore?.storeNumber || !orderDetails?.id) {
      if (!file) {
        handleSnackbarOpen(tOrderDetails('selectFile', 'Please select a file.'), 'error');
      } else {
        handleSnackbarOpen(tOrderDetails('uploadSpreadsheetError', 'Could not upload spreadsheet. Store or Order ID missing.'), 'error');
      }
      return;
    }

    setIsUploadingSpreadsheet(true);
    try {
      const base64Content = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const base64 = result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const request = {
        fileName: file.name,
        fileData: base64Content,
      };

      const result = await client.uploadOrderSpreadsheet(selectedStore.storeNumber, orderDetails.id.toString(), request);
      if (result.success) {
        handleSnackbarOpen(tOrderDetails('uploadSpreadsheetSuccess'), 'success');
      }
    } catch (error) {
      console.error('Failed to upload spreadsheet:', error);
    } finally {
      await fetchOrderDetails();
      setIsUploadingSpreadsheet(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getOptionLabel = (option: { parentName?: string; name: string }): string => {
    const parentName = option.parentName;
    const categoryName = option.name;

    const translatedParent = parentName && isOrderCategoryParent(parentName)
      ? t(OrdersCategoriesParentsMap[parentName as keyof typeof OrdersCategoriesParentsMap])
      : parentName;

    const translatedCategory = isOrderCategory(categoryName)
      ? t(OrderCategoriesTranslationMap[categoryName as keyof typeof OrderCategoriesTranslationMap])
      : categoryName;

    if (parentName) {
      return translatedParent
        ? `${translatedParent} > ${translatedCategory}`
        : `${parentName} > ${translatedCategory}`;
    } else {
      return translatedCategory;
    }
  };

  return (
    <Paper sx={{ display: 'flex', flexDirection: 'column', flex: 1, alignContent: 'center', my: '24px', py: '5px' }}>
      <input aria-hidden
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".xlsx,.xls,.csv"
        onChange={handleFileUpload}
      />
      <Box
        mb={6}
        px="24px"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box display="flex"
          alignItems="center">
          <Typography variant="textLG">{t('orderableItems')}</Typography>
          <Typography
            variant="textXS"
            sx={{
              border: `1px solid ${theme.palette.primary[400]}`,
              py: 2,
              px: 3,
              mx: 3,
              borderRadius: '6px',
              color: theme.palette.common.white,
              background: theme.palette.primary[500],
            }}
          >
            {totalCount} {t('items')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {canShowSpreadsheetActions() && (
            <>
              <Tooltip title={tOrderDetails('downloadSpreadsheetTooltip')}>
                <span>
                  <Button
                    variant="secondary"
                    disabled={loading || isDownloadingSpreadsheet}
                    size="lg"
                    onClick={handleDownloadSpreadsheet}
                    startIcon={<DownloadIcon />}
                    sx={{ minWidth: '120px' }}
                  >
                    {isDownloadingSpreadsheet ? tOrderDetails('loading', 'Loading...') : tOrderDetails('downloadSpreadsheet')}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={tOrderDetails('uploadSpreadsheetTooltip')}>
                <span>
                  <Button
                    variant="secondary"
                    disabled={loading || isUploadingSpreadsheet || !canEditOrder()}
                    size="lg"
                    onClick={handleUploadSpreadsheet}
                    startIcon={<UploadIcon />}
                    sx={{ minWidth: '120px' }}
                  >
                    {isUploadingSpreadsheet ? tOrderDetails('loading', 'Loading...') : tOrderDetails('uploadSpreadsheet')}
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
          <Tooltip title="Go to basket"
            arrow>
            <IconButton aria-label="Go to basket"
              onClick={() => navigate(`/order/${orderId}`)}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
                p: 1.5,
                borderRadius: '8px',
              }}
            >
              <ShoppingCartIcon />
            </IconButton>
          </Tooltip>
        </Box>

      </Box>
      <Box mt={4}
        sx={{
          boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
          border: `1px solid ${theme.palette.custom.gray[200]}`,
        }}>
        <Grid item
          container
          spacing={6}
          p={3}
        >
          <Grid item
            lg={3}
            xs={12}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('searchBy')}
              value={itemSearch}
              onChange={(e) => setItemSearch(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleSearch();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SvgIcon>
                      <SearchSm />
                    </SvgIcon>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item
            lg={3}
            xs={12}>
            <Autocomplete
              size="small"
              options={autocompleteOptions}
              getOptionLabel={getOptionLabel}
              value={categories.find(cat => cat.id === category) ?? autocompleteOptions[0]}
              onChange={(event, newValue) => {
                setCategory(newValue ? newValue.id : -1);
              }}
              renderInput={(params) => (
                <TextField {...params}
                  label={t('category')}
                  placeholder="Choose..." />
              )}
            />
          </Grid>

          <Grid item
            lg={3}
            xs={12}>
            <Button
              fullWidth
              variant="secondary"
              disabled={loading}
              onClick={clearFilters}
              size="lg"
            >
              {t('clearAll')}
            </Button>
          </Grid>

          <Grid item
            lg={3}
            xs={12}>
            <Button
              fullWidth
              variant="primary"
              disabled={loading}
              onClick={handleSearch}
              size="lg"
              startIcon={<SearchSm />}
            >
              {t('search')}
            </Button>
          </Grid>

        </Grid>
      </Box>

      <Grid item
        xs={12}
        py={2}>
        {loading ? (
          <LoadingWheel />
        ) :
          <>
            <TableContainer>
              <Table>
                <TableHead
                  sx={{
                    pt: '10px',
                  }}>
                  <TableRow>
                    <TableCell
                      sx={{ fontWeight: 'bold' }}
                      align="center">
                      {t('itemDetails')}
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 'bold' }}
                      align="left">
                      {t('quantity')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <ProductRow key={item.id}
                      product={item}
                      selectedOrderId={orderId}
                      orderItemId={orderDetails?.items.find(i => i.sku === item.sku)?.id}
                      basketQuantity={getBasketQuantity(item.id)}
                      promoBasketQuantity={getPromoBasketQuantity(item.id)}
                      promotion={getPromotionForItem(item)}
                      onAddPromoToOrder={onAddPromoToOrder}
                      predictedItem={predictedDemands.find(x => x.itemNumber === item.sku)}
                      showPredictedQuantity={showPredictedQuantity}
                      canEditPredictions={canEditPredictions}
                      onAddItemConfirmed={onAddItemConfirmed}
                      currencyCode={currencyCode}
                      showMessageWhenNoCost={showMessageWhenNoCost}
                      orderType={orderTypeId}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      ref={(ref: any) => {
                        productRowRefs.current[item.id] = ref!;
                      }}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer><CustomPagination
              page={page}
              setPage={setPage}
              maxPages={totalPages}
              breakpointForChangeDisplay={120000}
            ></CustomPagination>
          </>
        }
      </Grid>
      <MessagesSnackbar open={snackbar.open}
        onClose={handleSnackbarClose}
        message={snackbar.message}
        severity={snackbar.severity}
        duration={3000} />
    </Paper >
  );
};

export default Products;